// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Button, Group, Loader, Pagination, Stack, Table, Text } from '@mantine/core';
import type { Encounter } from '@medplum/fhirtypes';
import { IconNotes, IconPhone } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { formatAppointmentDate } from '../../utils/appointment-formatting';
import { PhoneEncounterModal } from './PhoneEncounterModal';
import { readPhoneEncounterNotes } from './phone-encounter';

interface Props {
  encounters: Encounter[];
  loading: boolean;
  patientId: string;
  onRefresh: () => void;
}

const ITEMS_PER_PAGE = 5;

function providerName(encounter: Encounter): string {
  return (
    encounter.participant?.find((item) => item.individual?.reference?.startsWith('Practitioner/'))?.individual
      ?.display ?? 'Provider'
  );
}

export function PhoneEncounterHistoryTable({ encounters, loading, patientId, onRefresh }: Props): JSX.Element | null {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Encounter>();

  if (loading) {
    return <Loader size="sm" />;
  }
  if (encounters.length === 0) {
    return null;
  }

  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const pageItems = encounters.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <>
      <Table highlightOnHover withTableBorder mt="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Type</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Date</Table.Th>
            <Table.Th>Time</Table.Th>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Documentation</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {pageItems.map((encounter) => {
            const noteCount = readPhoneEncounterNotes(encounter).length;
            const start = encounter.period?.start;
            return (
              <Table.Tr key={encounter.id}>
                <Table.Td>
                  <Group gap={6}>
                    <IconPhone size={14} />
                    <Text size="sm">Phone Encounter</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Badge color="#640040" variant="light" size="sm">
                    Completed
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatAppointmentDate(start)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {start ? new Date(start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{providerName(encounter)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {noteCount} {noteCount === 1 ? 'entry' : 'entries'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconNotes size={14} />}
                    onClick={() => setSelected(encounter)}
                  >
                    View Note
                  </Button>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      {encounters.length > ITEMS_PER_PAGE && (
        <Pagination total={Math.ceil(encounters.length / ITEMS_PER_PAGE)} value={page} onChange={setPage} mt="md" />
      )}
      {selected && (
        <PhoneEncounterModal
          opened
          encounter={selected}
          patient={{ resourceType: 'Patient', id: patientId }}
          onClose={() => setSelected(undefined)}
          onSaved={onRefresh}
        />
      )}
    </>
  );
}
